import React, { createContext, useContext, ReactNode } from 'react';
import { setSelectedOrganizationId } from '../redux/slices/organizationsSlice';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { RootState } from '../redux/store';

interface OrganizationFilterContextType {
    selectedOrganization: any;
    setSelectedOrganization: (organization: any) => void;
    clearSelectedOrganization: () => void;
}

const OrganizationFilterContext = createContext<OrganizationFilterContextType | undefined>(undefined);

interface OrganizationFilterProviderProps {
    children: ReactNode;
}

export const OrganizationFilterProvider: React.FC<OrganizationFilterProviderProps> = ({ children }) => {
    // El Cliente seleccionado persiste a través de todos los módulos del Backoffice:
    // navegar entre ellos nunca lo limpia. Solo se limpia con una acción manual del
    // usuario (ver `clearSelectedOrganization` y el botón de limpiar de cada combo).
    // Se lee directamente de Redux (sin estado local espejado por efecto) para que el
    // Cliente ya persistido esté disponible desde el primer render: un espejo via
    // useState + useEffect llega un render tarde, y los combos de Contrato/Organismo
    // que dependen de este valor ya hicieron su primer fetch sin el filtro (SMP-298).
    const { selectedOrganizationId } = useAppSelector((state: RootState) => state.organizations);
    const selectedOrganization = selectedOrganizationId;

    const dispatch = useAppDispatch();

    const setSelectedOrganization = (organization: any) => {
        const id = typeof organization === 'object' ? organization?.id ?? null : organization;
        dispatch(setSelectedOrganizationId(id));
    };

    const clearSelectedOrganization = () => {
        dispatch(setSelectedOrganizationId(null));
    };

    const value: OrganizationFilterContextType = {
        selectedOrganization,
        setSelectedOrganization,
        clearSelectedOrganization
    };

    return (
        <OrganizationFilterContext.Provider value={value}>
            {children}
        </OrganizationFilterContext.Provider>
    );
};

export const useOrganizationFilter = (): OrganizationFilterContextType => {
    const context = useContext(OrganizationFilterContext);
    if (context === undefined) {
        throw new Error('useOrganizationFilter must be used within an OrganizationFilterProvider');
    }
    return context;
}; 